import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHistory, faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import { ChangeTrackingData, FieldChange } from '../../types/article';
import changeTrackingService from '../../services/changeTrackingService';

interface ChangeIndicatorProps {
  fieldName: string;
  fieldPath: string;
  hasChanges: boolean;
  isNew?: boolean;
  children: React.ReactNode;
  articleNumber?: string;
  className?: string;
  positionOutside?: boolean; // NEW: Flag für außerhalb Position
}

export const ChangeIndicator: React.FC<ChangeIndicatorProps> = ({
  fieldName,
  fieldPath,
  hasChanges,
  isNew = false,
  children,
  articleNumber,
  className = '',
  positionOutside = false // NEW: Default ist innen
}) => {
  const [showHistory, setShowHistory] = useState(false);
  const [fieldHistory, setFieldHistory] = useState<FieldChange[]>([]);
  const [loading, setLoading] = useState(false);
  const [contextMenuPosition, setContextMenuPosition] = useState<{ x: number; y: number } | null>(null);
  const historyRef = useRef<HTMLDivElement>(null);

  console.log(`[CHANGE_INDICATOR] ${fieldName}:`, {
    hasChanges,
    isNew,
    fieldPath,
    articleNumber
  });

  const handleRightClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    console.log(`[RIGHT_CLICK] ${fieldName}:`, { 
      hasChanges, 
      isNew, 
      articleNumber,
      fieldPath
    });
    
    if (!hasChanges && !isNew) {
      console.log(`[RIGHT_CLICK] ${fieldName}: No changes/new - skipping`);
      return;
    }
    if (!articleNumber) {
      console.log(`[RIGHT_CLICK] ${fieldName}: No article number - skipping`);
      return;
    }

    console.log(`[RIGHT_CLICK] ${fieldName}: Opening history modal`);
    setContextMenuPosition({ x: e.clientX, y: e.clientY });
    setShowHistory(true);
    setLoading(true);

    try {
      console.log(`[FIELD_HISTORY] Loading for ${articleNumber}/${fieldPath}`);
      const history = await changeTrackingService.getFieldHistory(articleNumber, fieldPath);
      console.log(`[FIELD_HISTORY] ${fieldName} received:`, history);
      setFieldHistory(history);
    } catch (error) {
      console.error(`[FIELD_HISTORY] Error loading field history for ${fieldName}:`, error);
      setFieldHistory([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseHistory = () => {
    setShowHistory(false);
    setContextMenuPosition(null);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (historyRef.current && !historyRef.current.contains(event.target as Node)) {
        handleCloseHistory();
      }
    };

    if (showHistory) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showHistory]);

  const formatValue = (value: any): string => {
    if (value === null || value === undefined) return '-';
    if (typeof value === 'object') return JSON.stringify(value);
    if (typeof value === 'boolean') return value ? 'Ja' : 'Nein';
    return String(value);
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  let containerClass = '';
  let statusIndicator = null;
  let tooltip = '';

  // IMPORTANT: hasChanges takes precedence over isNew
  // If field has changes (multiple updates), it should show RED, not GREEN
  if (hasChanges) {
    tooltip = `Feld geändert: ${fieldName}. Rechtsklick für Historie.`;
    const positionClass = positionOutside 
      ? "absolute -right-3 top-1/2 transform -translate-y-1/2 z-10"
      : "absolute right-1 top-1/2 transform -translate-y-1/2 z-10";
    
    statusIndicator = (
      <div className={positionClass}>
        <div 
          className="w-2 h-2 bg-red-500 rounded-full cursor-pointer" 
          title={tooltip}
        />
      </div>
    );
  } else if (isNew) {
    tooltip = `Neues Feld: ${fieldName}. Rechtsklick für Details.`;
    const positionClass = positionOutside 
      ? "absolute -right-3 top-1/2 transform -translate-y-1/2 z-10"
      : "absolute right-1 top-1/2 transform -translate-y-1/2 z-10";
    
    statusIndicator = (
      <div className={positionClass}>
        <div 
          className="w-2 h-2 bg-green-500 rounded-full cursor-pointer" 
          title={tooltip}
        />
      </div>
    );
  }

  return (
    <>
      <div 
        className={`relative ${className}`}
        onContextMenu={handleRightClick}
        style={{ cursor: hasChanges || isNew ? 'context-menu' : 'default' }}
      >
        {children}
        {statusIndicator && (
          <div 
            onContextMenu={handleRightClick}
            style={{ cursor: 'context-menu' }}
          >
            {statusIndicator}
          </div>
        )}
      </div>

      {/* History Modal */}
      {showHistory && contextMenuPosition && (
        <div 
          ref={historyRef}
          className="fixed z-50 bg-white border border-gray-200 rounded-lg shadow-lg p-4 min-w-80 max-w-md"
          style={{
            left: Math.min(contextMenuPosition.x, window.innerWidth - 320),
            top: Math.min(contextMenuPosition.y, window.innerHeight - 400),
            maxHeight: '400px'
          }}
        >
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-semibold text-gray-900 flex items-center">
              <FontAwesomeIcon icon={faHistory} className="mr-2 text-blue-500" />
              Historie: {fieldName}
            </h4>
            <button
              onClick={handleCloseHistory}
              className="text-gray-400 hover:text-gray-600"
            >
              ×
            </button>
          </div>

          <div className="max-h-64 overflow-y-auto">
            {loading ? (
              <div className="text-center py-4">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500 mx-auto"></div>
                <p className="text-sm text-gray-500 mt-2">Lade Historie...</p>
              </div>
            ) : fieldHistory.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-4">
                Keine Historie verfügbar
              </p>
            ) : (
              <div className="space-y-3">
                {fieldHistory.map((change, index) => (
                  <div key={index} className="border-l-2 border-blue-200 pl-3 pb-3">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="text-xs text-gray-500 mb-1">
                          {formatDate(change.changedAt)} • {change.changedBy}
                        </div>
                        <div className="space-y-2">
                          <div>
                            <span className="text-xs font-medium text-red-600">Alt:</span>
                            <div className="text-sm text-gray-700 bg-red-50 p-2 rounded mt-1">
                              {formatValue(change.oldValue)}
                            </div>
                          </div>
                          <div>
                            <span className="text-xs font-medium text-green-600">Neu:</span>
                            <div className="text-sm text-gray-700 bg-green-50 p-2 rounded mt-1">
                              {formatValue(change.newValue)}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

interface ChangeTrackingWrapperProps {
  articleNumber: string;
  onChangesViewed?: () => void;
  children: (changeData: ChangeTrackingData & { markAsViewed: (userId: string) => Promise<void> }) => React.ReactNode;
}

export const ChangeTrackingWrapper: React.FC<ChangeTrackingWrapperProps> = ({
  articleNumber,
  onChangesViewed,
  children
}) => {
  const [changeData, setChangeData] = useState<ChangeTrackingData>({
    hasChanges: false,
    changedFields: [],
    changes: []
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadChangeData();
  }, [articleNumber]);

  const loadChangeData = async () => {
    if (!articleNumber) return;

    setLoading(true);
    try {
      const data = await changeTrackingService.getArticleChanges(articleNumber);
      setChangeData(data);
    } catch (error) {
      console.error('Error loading change data:', error);
      setChangeData({
        hasChanges: false,
        changedFields: [],
        changes: []
      });
    } finally {
      setLoading(false);
    }
  };

  const markChangesAsViewed = async (userId: string) => {
    try {
      await changeTrackingService.markChangesAsViewed(articleNumber, userId);
      await loadChangeData(); // Reload change data
      onChangesViewed?.();
    } catch (error) {
      console.error('Error marking changes as viewed:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        <span className="ml-3 text-gray-600">Lade Änderungen...</span>
      </div>
    );
  }

  // Enhanced change data with mark as viewed function
  const enhancedChangeData = {
    ...changeData,
    markAsViewed: markChangesAsViewed
  };

  return (
    <>
      {/* Changes Banner */}
      {changeData.hasChanges && (
        <div className="bg-orange-50 border-l-4 border-orange-400 p-4 mb-4">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <FontAwesomeIcon icon={faEyeSlash} className="h-5 w-5 text-orange-400" />
            </div>
            <div className="ml-3 flex-1">
              <h3 className="text-sm font-medium text-orange-800">
                Ungesehene Änderungen
              </h3>
              <div className="mt-2 text-sm text-orange-700">
                <p>
                  Der Lieferant hat {changeData.changedFields.length} Felder geändert. 
                  {changeData.lastViewedAt && (
                    <span className="block text-xs mt-1">
                      Zuletzt angesehen: {new Date(changeData.lastViewedAt).toLocaleDateString('de-DE')}
                    </span>
                  )}
                </p>
              </div>
              <div className="mt-3">
                <button
                  onClick={() => markChangesAsViewed('current-user')} // TODO: Get actual user ID
                  className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-orange-700 bg-orange-100 hover:bg-orange-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
                >
                  <FontAwesomeIcon icon={faEye} className="mr-1" />
                  Als angesehen markieren
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {children(enhancedChangeData)}
    </>
  );
};

export default ChangeIndicator;
